import { Injectable } from '@angular/core';
import { SharedMasterDataPharmacyChainSearchFilterSchema } from '@libs/shared/domain/master-data/pharmacy-chain/shared-master-data-pharmacy-chain-search-filter.schema';
import { SharedMasterDataPharmacyChainPropertiesSchema } from '@libs/shared/domain/master-data/pharmacy-chain/shared-master-data-pharmacy-chain.properties.schema';
import { HttpService } from '@services/http.service';
import { PharmacyChainRequestModel } from '@services/models/pharmacy-chain';
import { CursorPage } from '@shared/components/data-table/paginator';
import { ApiList } from '@shared/models/api-list.model';
import dayjs from 'dayjs';
import { catchError, map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PharmacyChainService extends HttpService {
  get(npi: string): Observable<SharedMasterDataPharmacyChainPropertiesSchema | undefined> {
    return this.httpClient
      .get<{ pharmacyChain: SharedMasterDataPharmacyChainPropertiesSchema }>(`${this.urlService.MASTER_DATA_PHARMACY_CHAINS_URL}/${npi}`)
      .pipe(
        map(result => result.pharmacyChain),
        catchError(error => this.handleGetError(error)),
      );
  }

  create(data: PharmacyChainRequestModel): Observable<SharedMasterDataPharmacyChainPropertiesSchema> {
    return this.httpClient
      .post<{ pharmacyChain: SharedMasterDataPharmacyChainPropertiesSchema }>(this.urlService.MASTER_DATA_PHARMACY_CHAINS_URL, data)
      .pipe(map(result => result.pharmacyChain));
  }

  update(npi: string, data: PharmacyChainRequestModel): Observable<SharedMasterDataPharmacyChainPropertiesSchema> {
    return this.httpClient
      .patch<{
        pharmacyChain: SharedMasterDataPharmacyChainPropertiesSchema;
      }>(`${this.urlService.MASTER_DATA_PHARMACY_CHAINS_URL}/${npi}`, data)
      .pipe(map(result => result.pharmacyChain));
  }

  search(
    filter: SharedMasterDataPharmacyChainSearchFilterSchema,
    page?: CursorPage,
  ): Observable<ApiList<SharedMasterDataPharmacyChainPropertiesSchema>> {
    return this.httpClient
      .post<ApiList<SharedMasterDataPharmacyChainPropertiesSchema>>(`${this.urlService.MASTER_DATA_PHARMACY_CHAINS_URL}/search`, {
        filter,
        page,
      })
      .pipe(catchError(error => this.handleSearchError<SharedMasterDataPharmacyChainPropertiesSchema>(error)));
  }

  parseBankLetterFiles(bankLetterFiles: string[]): { original: string; display: string }[] {
    return bankLetterFiles.map(fileName => {
      const match = fileName.match(/_(\d{10,13})(\.[a-zA-Z0-9]+)?$/);

      if (match && match[1]) {
        const millis = match[1].length >= 13 ? +match[1] : +match[1] * 1000;
        const dateString = dayjs(millis).format('MM/DD/YYYY');

        return { original: fileName, display: `Bank Letter ${dateString}` };
      }

      return { original: fileName, display: fileName };
    });
  }

  parseAuthorizationFiles(authorizationAttachments: string[], npi = 'UNKNOWN'): { original: string; display: string }[] {
    return authorizationAttachments.map(fileName => ({
      original: this.parseAuthorizationAttachmentName(fileName, npi),
      display: fileName,
    }));
  }

  parseAuthorizationAttachmentName(fileName: string, npi = 'UNKNOWN'): string {
    return `pharmacy_chain_${npi}_${fileName}`;
  }
}
